<template>
  <a-modal
    :visible="visible"
    :title="$t('topBar.storage.modalTitle')"
    :footer="null"
    width="760px"
    destroy-on-close
    @cancel="handleClose"
  >
    <p class="mb-3 text-muted small storage-scope-description">
      {{ $t("topBar.storage.scopeDescription") }}
    </p>

    <div v-if="hasLimit" :class="$style.quotaCard">
      <div
        class="d-flex justify-content-between align-items-baseline flex-wrap mb-2"
      >
        <span class="font-weight-bold">{{ $t("topBar.storage.quotaTitle") }}</span>
        <span
          v-if="latestScan && latestScan.totalBytes != null"
          class="text-nowrap small text-muted"
        >
          {{
            $t("topBar.storage.quotaUsedOf", {
              used: formatBytes(latestScan.totalBytes),
              allowed: formatBytes(storageLimitBytes),
            })
          }}
        </span>
        <span v-else class="small text-muted">{{ $t("topBar.storage.noData") }}</span>
      </div>

      <a-progress
        v-if="latestScan && latestScan.totalBytes != null"
        :percent="barPercent"
        :status="barStatus"
        :stroke-width="14"
      />
      <div v-else class="text-muted small py-2">{{ $t("topBar.storage.noData") }}</div>

      <div
        v-if="latestScan && latestScan.objectCount != null"
        class="small text-muted mt-2"
      >
        {{
          $t("topBar.storage.objectsInLastScan", {
            count: latestScan.objectCount,
          })
        }}
      </div>

      <a-alert
        v-if="showOverAlert"
        type="error"
        show-icon
        class="mt-3"
        :message="$t('topBar.storage.quotaOver')"
      />
      <a-alert
        v-else-if="showNearAlert"
        type="warning"
        show-icon
        class="mt-3"
        :message="$t('topBar.storage.quotaNear')"
      />
    </div>

    <div v-else-if="latestScan" class="mb-3 text-muted small">
      {{
        $t("topBar.storage.quotaUnlimitedLine", {
          used: formatBytes(latestScan.totalBytes),
        })
      }}
      <template v-if="latestScan.objectCount != null">
        ·
        {{
          $t("topBar.storage.objectsInLastScan", {
            count: latestScan.objectCount,
          })
        }}
      </template>
    </div>
    <div v-else class="mb-3 text-muted small">
      {{ $t("topBar.storage.noData") }}
    </div>

    <div v-if="chartLoading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <apexchart
        :key="chartRenderKey"
        type="area"
        height="320"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import {
  clampedBarPercent as computeClampedBarPercent,
  storageProgressStatus,
} from "@/utilities/storageQuota";

export default {
  name: "StorageUsageModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** Pack storage cap in bytes; omit or null/-1 from parent means unlimited */
    storageLimitBytes: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      latestScan: null,
      chartLoading: false,
      historyItems: [],
      chartSeries: [{ name: "", data: [] }],
      chartOptions: {},
      chartRenderKey: 0,
    };
  },
  computed: {
    hasLimit() {
      const l = this.storageLimitBytes;
      return l != null && l > 0 && !Number.isNaN(Number(l));
    },
    barPercent() {
      if (!this.hasLimit || !this.latestScan || this.latestScan.totalBytes == null) {
        return 0;
      }
      return computeClampedBarPercent(
        this.latestScan.totalBytes,
        this.storageLimitBytes
      );
    },
    barStatus() {
      if (!this.hasLimit || !this.latestScan || this.latestScan.totalBytes == null) {
        return "normal";
      }
      return storageProgressStatus(
        this.latestScan.totalBytes,
        this.storageLimitBytes
      );
    },
    showOverAlert() {
      if (!this.hasLimit || !this.latestScan || this.latestScan.totalBytes == null) {
        return false;
      }
      return Number(this.latestScan.totalBytes) > this.storageLimitBytes;
    },
    showNearAlert() {
      if (
        !this.hasLimit ||
        !this.latestScan ||
        this.latestScan.totalBytes == null ||
        this.showOverAlert
      ) {
        return false;
      }
      const u = Number(this.latestScan.totalBytes);
      const p = u / this.storageLimitBytes;
      return p >= 0.9 && p <= 1;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadModalData();
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit("update:visible", false);
    },
    formatBytes(bytes) {
      if (bytes == null || Number.isNaN(Number(bytes))) return "—";
      const n = Number(bytes);
      const units = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let v = n;
      while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
      }
      const decimals = i === 0 ? 0 : v < 10 ? 2 : 1;
      return `${v.toFixed(decimals)} ${units[i]}`;
    },
    async loadModalData() {
      this.chartLoading = true;
      await this.fetchLatestScan();
      try {
        const { data } = await apiClient.get("/dbUsageScans/history", {
          params: { limit: 200, order: "asc" },
        });
        this.historyItems = data.items || [];
        this.applyChartFromHistory();
      } catch (e) {
        this.historyItems = [];
        this.applyChartFromHistory();
      } finally {
        this.chartLoading = false;
        this.chartRenderKey += 1;
      }
    },
    async fetchLatestScan() {
      try {
        const { data } = await apiClient.get("/dbUsageScans/latest");
        this.latestScan = data;
      } catch (e) {
        this.latestScan = null;
      }
    },
    storageLimitAnnotation() {
      if (!this.hasLimit || this.storageLimitBytes == null) {
        return { yaxis: [] };
      }
      const y = Number(this.storageLimitBytes);
      if (!Number.isFinite(y) || y <= 0) {
        return { yaxis: [] };
      }
      return {
        yaxis: [
          {
            y,
            borderColor: "#ff4d4f",
            strokeDashArray: 6,
            label: {
              borderColor: "#ff4d4f",
              style: {
                color: "#fff",
                background: "#ff4d4f",
              },
              text: this.$t("topBar.storage.chartLimitLabel"),
            },
          },
        ],
      };
    },
    chartYAxisMax(byteValues) {
      const nums = (byteValues || []).map((b) => Number(b)).filter((n) => !Number.isNaN(n));
      const dataMax = nums.length ? Math.max(...nums) : 0;
      if (!this.hasLimit || this.storageLimitBytes == null) {
        return dataMax > 0 ? dataMax * 1.05 : undefined;
      }
      const limit = Number(this.storageLimitBytes);
      if (!Number.isFinite(limit) || limit <= 0) {
        return dataMax > 0 ? dataMax * 1.05 : undefined;
      }
      const m = Math.max(dataMax, limit);
      return m > 0 ? m * 1.08 : undefined;
    },
    applyChartFromHistory() {
      const items = this.historyItems;
      const fmt = (b) => this.formatBytes(b);
      const name = this.$t("topBar.storage.seriesStorage");
      const annotations = this.storageLimitAnnotation();
      const yMaxEmpty = this.chartYAxisMax([]);
      if (!items.length) {
        this.chartSeries = [{ name, data: [] }];
        this.chartOptions = {
          chart: {
            type: "area",
            toolbar: { show: false },
            zoom: { enabled: false },
          },
          dataLabels: { enabled: false },
          stroke: { curve: "smooth", width: 2 },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 0.4,
              opacityFrom: 0.5,
              opacityTo: 0.05,
            },
          },
          xaxis: { categories: [] },
          yaxis: {
            max: yMaxEmpty,
            labels: {
              formatter: (val) => fmt(val),
            },
          },
          annotations,
          tooltip: {
            y: {
              formatter: (val) => fmt(val),
            },
          },
          noData: {
            text: this.$t("topBar.storage.noData"),
          },
          colors: ["#1890ff"],
        };
        return;
      }
      const categories = items.map((i) =>
        moment(i.scannedAt).format("DD/MM/YYYY HH:mm")
      );
      const bytes = items.map((i) => i.totalBytes);
      const yMax = this.chartYAxisMax(bytes);
      this.chartSeries = [{ name, data: bytes }];
      this.chartOptions = {
        chart: {
          type: "area",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
          zoom: { enabled: true },
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.4,
            opacityFrom: 0.55,
            opacityTo: 0.08,
          },
        },
        markers: { size: 4, hover: { size: 6 } },
        xaxis: {
          categories,
          labels: {
            rotate: items.length > 10 ? -45 : 0,
            rotateAlways: items.length > 10,
          },
        },
        yaxis: {
          max: yMax,
          labels: {
            formatter: (val) => fmt(val),
          },
        },
        annotations,
        tooltip: {
          x: {
            formatter: (val, opts) =>
              categories[opts.dataPointIndex] || val,
          },
          y: {
            formatter: (val) => fmt(val),
          },
        },
        colors: ["#1890ff"],
        legend: { show: true },
      };
    },
  },
};
</script>

<style lang="scss" module>
.quotaCard {
  padding: 16px 18px;
  margin-bottom: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(
    145deg,
    rgba(24, 144, 255, 0.06) 0%,
    rgba(0, 0, 0, 0.02) 100%
  );
}

[data-kit-theme="dark"] .quotaCard {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}
</style>

<style scoped>
.storage-scope-description {
  line-height: 1.5;
  font-size: 13px;
}
</style>
